import React, { Component } from 'react';
import PropTypes from 'prop-types';
import './ErrorBoundary.css';

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorCount: 0
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log error details to state
    this.setState(prevState => ({
      error,
      errorInfo,
      errorCount: prevState.errorCount + 1
    }));

    // Log error to console for development
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    // TODO: Log to error tracking service (e.g., Sentry, Application Insights)
    this.logErrorToService(error, errorInfo);
  }

  logErrorToService = (error, errorInfo) => {
    // Placeholder for error tracking service integration
    // Example implementations:
    
    // For Sentry:
    // if (window.Sentry) {
    //   window.Sentry.captureException(error, {
    //     extra: errorInfo,
    //     tags: { errorBoundary: true }
    //   });
    // }

    // For Azure Application Insights:
    // if (window.appInsights) {
    //   window.appInsights.trackException({
    //     error: error,
    //     properties: {
    //       componentStack: errorInfo.componentStack,
    //       errorBoundary: true
    //     }
    //   });
    // }

    // Basic logging to track errors in localStorage (for development/debugging)
    try {
      const errorLog = {
        timestamp: new Date().toISOString(),
        message: error.toString(),
        stack: error.stack,
        componentStack: errorInfo.componentStack,
        userAgent: navigator.userAgent,
        url: window.location.href
      };
      
      const existingLogs = JSON.parse(localStorage.getItem('errorLogs') || '[]');
      existingLogs.push(errorLog);
      
      // Keep only last 10 errors to avoid localStorage overflow
      if (existingLogs.length > 10) {
        existingLogs.shift();
      }
      
      localStorage.setItem('errorLogs', JSON.stringify(existingLogs));
    } catch (e) {
      console.error('Failed to log error to localStorage:', e);
    }
  };

  handleReload = () => {
    // Clear error state and reload the page
    window.location.reload();
  };

  handleGoHome = () => {
    // Reset error state and navigate to home
    this.setState({ 
      hasError: false, 
      error: null, 
      errorInfo: null 
    });
    window.location.href = '/sage';
  };

  handleGoBack = () => {
    // Try to go back in browser history
    window.history.back();
  };

  handleReset = () => {
    // Reset error boundary state without reload (useful for dynamic errors)
    this.setState({ 
      hasError: false, 
      error: null, 
      errorInfo: null 
    });
  };

  render() {
    if (this.state.hasError) {
      // Fallback UI when error occurs
      return (
        <div className="error-boundary-container">
          <div className="error-boundary-content">
            <div className="error-icon">
              <svg width="80" height="80" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" stroke="#ef4444" strokeWidth="2"/>
                <path d="M12 8V12" stroke="#ef4444" strokeWidth="2" strokeLinecap="round"/>
                <circle cx="12" cy="16" r="1" fill="#ef4444"/>
              </svg>
            </div>
            
            <h1 className="error-title">Oops! Something went wrong</h1>
            <p className="error-description">
              We encountered an unexpected error. Don't worry, your data is safe. 
              Please try one of the recovery options below.
            </p>

            <div className="error-actions">
              <button 
                className="error-button error-button-primary" 
                onClick={this.handleReload}
                title="Reload the page"
                aria-label="Reload the page"
                autoFocus
              >
                <span className="button-icon">🔄</span>
                Reload Page
              </button>
              
              <button 
                className="error-button error-button-secondary" 
                onClick={this.handleGoHome}
                title="Go to home page"
                aria-label="Go to home page"
              >
                <span className="button-icon">🏠</span>
                Go to Home
              </button>
              
              <button 
                className="error-button error-button-secondary" 
                onClick={this.handleGoBack}
                title="Go back to previous page"
                aria-label="Go back to previous page"
              >
                <span className="button-icon">⬅️</span>
                Go Back
              </button>

              {this.state.errorCount > 1 && (
                <button 
                  className="error-button error-button-tertiary" 
                  onClick={this.handleReset}
                  title="Try to recover without reloading"
                  aria-label="Try recovery without reloading"
                >
                  <span className="button-icon">🔧</span>
                  Try Recovery
                </button>
              )}
            </div>

            {/* Technical details - collapsible for developers */}
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="error-details">
                <summary className="error-details-summary">
                  Technical Details (Development Only)
                </summary>
                <div className="error-details-content">
                  <div className="error-section">
                    <h3>Error Message:</h3>
                    <pre className="error-code">{this.state.error.toString()}</pre>
                  </div>
                  {this.state.error.stack && (
                    <div className="error-section">
                      <h3>Stack Trace:</h3>
                      <pre className="error-code">{this.state.error.stack}</pre>
                    </div>
                  )}
                  {this.state.errorInfo && this.state.errorInfo.componentStack && (
                    <div className="error-section">
                      <h3>Component Stack:</h3>
                      <pre className="error-code">{this.state.errorInfo.componentStack}</pre>
                    </div>
                  )}
                </div>
              </details>
            )}

            <p className="error-help-text">
              If this problem persists, please contact support with the error details.
            </p>
          </div>
        </div>
      );
    }

    // Normally, render children
    return this.props.children;
  }
}

ErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
};

export default ErrorBoundary;
